import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
  styled,
} from '@mui/material';
import { memo, useCallback } from 'react';

import { useConnectNowContext } from '../../hooks/useConnectNowContext';
import { I18nAccess, useTranslation } from '../../hooks/useKeyedTranslation';
import { OFD_CLINICS } from '../../utils/data';

const StyledStack = styled(Stack)(() => ({
  height: '100%',
  alignItems: 'center',
  background: 'rgba(255, 255, 255, 0.8)',
  boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.25)',
  borderRadius: '16px',
  padding: '1.5em',
  flexGrow: 1,
  justifyContent: 'space-between',
  maxHeight: '300px',
  backdropFilter: 'blur(6.7957px)',
}));

const StyledTypography = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  fontWeight: 400,
  fontStyle: 'italic',
  lineHeight: '1.1875rem',
  color: theme.palette.primary.main,
}));

export interface SelectClinicProps {
  text: I18nAccess;
  label: I18nAccess;
}

function SelectClinic({ text, label }: SelectClinicProps) {
  const { t } = useTranslation();
  const { state, onSelectClinicHandler, onContinueClinicHandler } =
    useConnectNowContext();

  const onSelectHandler = useCallback(
    (event: SelectChangeEvent) => {
      const selectedOption = event.target.value;
      onSelectClinicHandler(selectedOption);
    },
    [onSelectClinicHandler]
  );

  return (
    <StyledStack spacing={3}>
      <Stack spacing={2} maxWidth="100%">
        <StyledTypography>{t(text)}</StyledTypography>
        <FormControl fullWidth>
          <InputLabel id="select-question">{t(label)}</InputLabel>
          <Select
            labelId="select-question"
            value={state.selectedOption}
            onChange={onSelectHandler}
            label={t(label)}
          >
            {OFD_CLINICS.map((option) => (
              <MenuItem value={option.name} key={option.name}>
                {option.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      <Box display="flex" justifyContent="center" width="100%">
        <Button
          role="button"
          type="button"
          variant="contained"
          color="primary"
          title={t('connect_now.continue_button.title')}
          aria-label={t('connect_now.continue_button.aria_label')}
          onClick={() => onContinueClinicHandler(OFD_CLINICS)}
          disabled={!state.selectedOption}
        >
          {t('connect_now.continue_button.label')}
        </Button>
      </Box>
    </StyledStack>
  );
}

export default memo(SelectClinic);
